import moment from 'moment';
import 'moment-timezone';
import { Availability } from './availability.types';
import { DayHeaderState } from './components/DayHeader/DayHeader.types';
import { isClosedWeekday } from './PickTime.helpers';
import { WeekDay, WeekViewModel, WeekViewSlot, WeekViewState } from './PickTimeWeekView.types';

/**
 * Pure availability → week-view-model mapping for the `_CVPickTimeWeekView`
 * grid.
 *
 * Renders 7 consecutive days from a caller-supplied `weekStart`; the container
 * owns the week anchor and shifts it on prev/next (legacy WeekPicker.js keeps a
 * `startFrom` ms cursor and shifts it ±7 days). It does NOT read
 * `availability.firstDay` — that field is typed `number` but the runtime payload
 * is an object (`getDayInfo(...)`), so it is a latent landmine this
 * presentational contract sidesteps entirely.
 *
 * The day-state and campaign classification is replicated from `buildCalendar`
 * (PickTimeCalendar.helpers.ts) rather than shared, to keep the merged calendar
 * view untouched (accepted ~5-line duplication). No fetching and no i18n — the
 * weekday/month labels are formatted in the view; only the tz-stable
 * time-of-day ("HH:mm") is derived here. `referenceTime` is injected so
 * today/past resolution is deterministic and testable.
 */

// Build a moment from a ms timestamp in the place tz when one is provided, else
// the runtime-local tz. Day-keys derived from ms MUST share the parseZone slot
// day-key basis below, or days near midnight get misclassified when the viewer's
// tz differs from the place. (Same convention as buildCalendar.)
const momentInTz = (ms: number, tz?: string): moment.Moment => (tz ? moment.tz(ms, tz) : moment(ms));
const dayKeyFromMs = (ms: number, tz?: string): string => momentInTz(ms, tz).format('YYYY-MM-DD');
const dayKeyFromIso = (iso: string): string => moment.parseZone(iso).format('YYYY-MM-DD');
const slotMs = (iso: string): number => moment.parseZone(iso).valueOf();

export type BuildWeekOptions = {
  /** Ms timestamp of the visible week's first column — supplied by the caller. */
  weekStart: number;
  /** "Now" (ms); resolves today and past slots/days. */
  referenceTime: number;
  /** IANA place timezone (e.g. "Europe/Stockholm") used to derive day-keys; defaults to the runtime-local tz. */
  placeTimezone?: string;
  /** Campaign window start as unix SECONDS (legacy WeekPicker.js convention). */
  campaignStart?: number;
  /** Campaign window end as unix SECONDS. */
  campaignEnd?: number;
  /**
   * Whether slots carry a "spots left" count — group bookings only, where the
   * ERP capacity is the seats still free rather than the constant 1 every
   * single-seat slot reports.
   */
  showSpotsLeft?: boolean;
  /**
   * Render slots without a price line. The last-minute flow needs this: the
   * availability endpoint prices its slots at list price, so a label here would
   * contradict the discounted price the service list shows. Legacy's last-minute
   * picker showed no slot price either.
   */
  hidePrices?: boolean;
  /**
   * Weekdays (0 = Sunday) the place's weekly schedule opens on. A day without
   * slots reads as closed only on a weekday outside it; omitted, no such day
   * reads as closed.
   */
  openWeekdays?: number[];
};

export const buildWeek = (availability: Availability | undefined, options: BuildWeekOptions): WeekViewModel => {
  const {
    weekStart,
    referenceTime,
    placeTimezone,
    campaignStart,
    campaignEnd,
    showSpotsLeft,
    hidePrices,
    openWeekdays,
  } = options;

  const slots = Object.values(availability?.calendarTimeSlots ?? {});
  const openDays = new Set((availability?.fromErpOverview?.datesWithOpeningHours ?? []).map(dayKeyFromIso));
  const todayKey = dayKeyFromMs(referenceTime, placeTimezone);

  const anchor = momentInTz(weekStart, placeTimezone).startOf('day');

  const days: WeekDay[] = Array.from({ length: 7 }, (_, index) => {
    const dayKey = anchor.clone().add(index, 'days').format('YYYY-MM-DD');
    const isPast = dayKey < todayKey;
    // The campaign window is UTC-midnight unix seconds (legacy compares against
    // moment.utc().startOf('day')), so read the date as UTC midnight to match.
    const daySeconds = moment.utc(dayKey, 'YYYY-MM-DD').unix();
    const hasCampaign = !!campaignStart && !!campaignEnd && daySeconds >= campaignStart && daySeconds <= campaignEnd;

    const daySlots: WeekViewSlot[] = slots
      .filter((s) => dayKeyFromIso(s.start) === dayKey)
      .sort((a, b) => slotMs(a.start) - slotMs(b.start))
      .map((s): WeekViewSlot => {
        const employeePrices = hidePrices ? undefined : s.employeePrices;
        const finalPriceLabel = employeePrices?.finalPriceLabel;
        return {
          time: moment.parseZone(s.start).format('HH:mm'),
          price: finalPriceLabel ?? employeePrices?.priceLabel,
          type: finalPriceLabel ? 'reduced' : 'default',
          disabled: slotMs(s.start) < referenceTime,
          spotsLeft: showSpotsLeft ? s.capacity : undefined,
          raw: s,
        };
      });

    const availableCount = daySlots.filter((s) => !s.disabled).length;

    let state: DayHeaderState;
    if (isPast) state = 'default'; // no 'past' header variant — render plainly; its slots are disabled below.
    else if (availableCount) state = hasCampaign ? 'campaign' : 'default';
    else state = openDays.has(dayKey) || !isClosedWeekday(dayKey, openWeekdays) ? 'fullyBooked' : 'closed';

    return { date: dayKey, state, isToday: dayKey === todayKey, availableCount, slots: daySlots };
  });

  // Scoped to the 7 rendered days, not every future slot in the payload — the
  // payload can carry nearest-availability slots for a later week, which must
  // not mark *this* week 'loaded'.
  const state: WeekViewState = days.some((d) => d.availableCount > 0) ? 'loaded' : 'empty';

  return { days, state, nextAvailableDate: availability?.fromErpOverview?.firstAvailableDate };
};
